import { Dialog } from "primereact/dialog";
import { useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { baseUrl, Fetch } from "../../shared/Fetch";
import { JVDropdown } from "../../shared/JVDropdown";
import { IContact } from "../../models/BaseModel";
import { showDateTime } from "../../utilities/helper";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";

interface IProps {
    contacts: IContact[];
    open: boolean;
    hideDialog: () => void;
    toast: any;
    reloadData: () => void;
    hasPermission: (pageName: string, featureName?: string) => boolean;
}

interface ISendWhtsappMsg {
    templateName?: string | null;
    to: string[] | null;
}

const schema = yup.object({
    templateName: yup.string().nullable()
});

interface ILastWpMessage {
    id: number;
    name: string;
    phoneNumber: string;
    lastWpMessage?: {
        wpTemplateName: string;
        sendAt: string;
    } | null;
    selected?: boolean;
}

type ExcludePeriod = "today" | "week" | "month" | null;

export const WhatsppSendTemplate = (props: IProps) => {

    const [lastWpMessages, setLastWpMessages] = useState<{
        contacts: ILastWpMessage[];
        loading: boolean;
    }>({
        contacts: [],
        loading: false
    });

    const [loading, setLoading] = useState(false);
    const [wpTemplateList, setWpTemplateList] = useState<any[]>([]);
    const [excludePeriod, setExcludePeriod] =
        useState<ExcludePeriod>(null);

    const [search, setSearch] = useState("");

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        watch
    } = useForm<ISendWhtsappMsg>({
        resolver: yupResolver(schema),
        defaultValues: {},
        shouldFocusError: false
    });

    const templateName = watch("templateName");

    // ---------------------------------------------------------
    // Load templates
    // ---------------------------------------------------------

    useEffect(() => {
        loadWpTemplateList();
    }, []);

    // ---------------------------------------------------------
    // Load previous messages
    // ---------------------------------------------------------

    useEffect(() => {

        if (
            props.open &&
            templateName &&
            props.contacts.length > 0
        ) {
            const contactIds =
                props.contacts.map(contact => contact.id!);

            loadLastWpMessages(
                templateName,
                contactIds
            );
        }

        if (!templateName) {
            setLastWpMessages({
                contacts: [],
                loading: false
            });

            setExcludePeriod(null);
        }

    }, [
        props.open,
        props.contacts,
        templateName
    ]);

    // ---------------------------------------------------------
    // API
    // ---------------------------------------------------------

    const loadLastWpMessages = async (
        templateName: string,
        contactIds: number[]
    ) => {

        setLastWpMessages({
            contacts: [],
            loading: true
        });

        setExcludePeriod(null);

        const response = await Fetch(
            "contact/wp",
            "GET",
            null,
            `templateName=${encodeURIComponent(templateName)}&contactIds=${encodeURIComponent(JSON.stringify(contactIds))}`,
            props.toast
        );

        if (response) {

            const data = await response.json();

            setLastWpMessages({
                contacts: data.map((item: ILastWpMessage) => ({
                    ...item,
                    selected: true
                })),
                loading: false
            });

        } else {

            setLastWpMessages({
                contacts: [],
                loading: false
            });
        }
    };

    const loadWpTemplateList = async () => {

        const response = await Fetch(
            "wpTemplate/dropdown",
            "GET",
            null,
            null,
            props.toast
        );

        if (response) {
            const data = await response.json();
            setWpTemplateList(data);
        }
    };

    // ---------------------------------------------------------
    // Date helpers
    // ---------------------------------------------------------

    const isToday = (dateString: string) => {

        const date = new Date(dateString);
        const today = new Date();

        return (
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate()
        );
    };

    const isThisWeek = (dateString: string) => {

        const date = new Date(dateString);
        const today = new Date();

        const day = today.getDay();

        const diffToMonday =
            day === 0 ? 6 : day - 1;

        const startOfWeek = new Date(today);

        startOfWeek.setHours(0, 0, 0, 0);
        startOfWeek.setDate(
            today.getDate() - diffToMonday
        );

        const endOfWeek = new Date(startOfWeek);

        endOfWeek.setDate(
            startOfWeek.getDate() + 7
        );

        return (
            date >= startOfWeek &&
            date < endOfWeek
        );
    };

    const isThisMonth = (dateString: string) => {

        const date = new Date(dateString);
        const today = new Date();

        return (
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth()
        );
    };

    // ---------------------------------------------------------
    // Exclude contacts
    // ---------------------------------------------------------

    const excludeContacts = (
        period: ExcludePeriod
    ) => {

        if (!period) {
            return;
        }

        setLastWpMessages(prev => {

            const contacts = prev.contacts.map(contact => {

                if (!contact.lastWpMessage) {
                    return {
                        ...contact,
                        selected: true
                    };
                }

                const sendAt =
                    contact.lastWpMessage.sendAt;

                let shouldExclude = false;

                switch (period) {

                    case "today":
                        shouldExclude =
                            isToday(sendAt);
                        break;

                    case "week":
                        shouldExclude =
                            isThisWeek(sendAt);
                        break;

                    case "month":
                        shouldExclude =
                            isThisMonth(sendAt);
                        break;
                }

                return {
                    ...contact,
                    selected: !shouldExclude
                };
            });

            return {
                ...prev,
                contacts
            };
        });

        setExcludePeriod(period);
    };

    // ---------------------------------------------------------
    // Selection
    // ---------------------------------------------------------

    const handleContactSelect = (
        contactId: number,
        selected: boolean
    ) => {

        setLastWpMessages(prev => ({
            ...prev,

            contacts: prev.contacts.map(contact =>
                contact.id === contactId
                    ? {
                        ...contact,
                        selected
                    }
                    : contact
            )
        }));
    };

    const selectAll = () => {

        setLastWpMessages(prev => ({
            ...prev,

            contacts: prev.contacts.map(contact => ({
                ...contact,
                selected: true
            }))
        }));

        setExcludePeriod(null);
    };

    const clearAll = () => {

        setLastWpMessages(prev => ({
            ...prev,

            contacts: prev.contacts.map(contact => ({
                ...contact,
                selected: false
            }))
        }));

        setExcludePeriod(null);
    };

    // ---------------------------------------------------------
    // Filter
    // ---------------------------------------------------------

    const filteredContacts = useMemo(() => {

        const value = search
            .trim()
            .toLowerCase();

        if (!value) {
            return lastWpMessages.contacts;
        }

        return lastWpMessages.contacts.filter(contact =>
            contact.name
                ?.toLowerCase()
                .includes(value) ||

            contact.phoneNumber
                ?.toLowerCase()
                .includes(value)
        );

    }, [
        lastWpMessages.contacts,
        search
    ]);

    // ---------------------------------------------------------
    // Counts
    // ---------------------------------------------------------

    const selectedCount =
        lastWpMessages.contacts.filter(
            x => x.selected
        ).length;

    const totalCount =
        lastWpMessages.contacts.length;

    const sentCount =
        lastWpMessages.contacts.filter(
            x => x.lastWpMessage
        ).length;

    const notSentCount =
        totalCount - sentCount;

    // ---------------------------------------------------------
    // Submit
    // ---------------------------------------------------------

    const onSubmit = async (
        body: ISendWhtsappMsg
    ) => {

        if (selectedCount === 0) {

            props.toast.current?.show({
                severity: "warn",
                summary: "No contacts selected",
                detail: "Please select at least one contact.",
                life: 3000
            });

            return;
        }

        setLoading(true);

        const contactNumbers =
            lastWpMessages.contacts
                .filter(contact => contact.selected)
                .map(contact => contact.phoneNumber);

        const payload = {
            templateName: body.templateName,
            to: contactNumbers
        };

        const response = await fetch(
            `${baseUrl}WhatsAppWebhook/sendTempWhatsappMessage`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            }
        );

        if (response.ok) {

            const result: {
                sent: number;
                notSent: number;
                message: string;
            } = await response.json();

            props.toast.current?.show({
                severity:
                    result.notSent > 0
                        ? "warn"
                        : "success",

                summary: "WhatsApp",
                detail: result.message,
                life: 3000
            });

            props.reloadData();
            onHide();

        } else {

            props.toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to send message!",
                life: 3000
            });
        }

        setLoading(false);
    };

    // ---------------------------------------------------------
    // Clear
    // ---------------------------------------------------------

    const onClear = () => {

        reset();

        setLoading(false);

        setSearch("");

        setExcludePeriod(null);
    };

    // ---------------------------------------------------------
    // Hide
    // ---------------------------------------------------------

    const onHide = () => {

        onClear();

        setLastWpMessages({
            contacts: [],
            loading: false
        });

        props.hideDialog();
    };

    return (
        <Dialog
            style={{
                width: "650px",
                maxWidth: "95vw"
            }}
            visible={props.open}
            onHide={onHide}
            header="Send WhatsApp"
            modal
        >

            <form
                onSubmit={handleSubmit(onSubmit)}
                className="p-fluid"
            >

                {/* ------------------------------------------ */}
                {/* Template */}
                {/* ------------------------------------------ */}

                <div className="mb-3">

                    <JVDropdown
                        column={1}
                        className="m-0 p-0"
                        label="WhatsApp Template"
                        control={control}
                        options={wpTemplateList}
                        optionLabel="name"
                        optionValue="name"
                        name="templateName"
                        placeholder="Select Template"
                    />

                </div>

                {/* ------------------------------------------ */}
                {/* Contact summary */}
                {/* ------------------------------------------ */}

                {templateName && (
                    <div className="surface-100 border-round p-3 mb-3">

                        <div className="flex justify-content-between align-items-center">

                            <div>
                                <div className="text-900 font-semibold">
                                    Recipients
                                </div>

                                <small className="text-600">
                                    Select contacts who should receive this template.
                                </small>
                            </div>

                            <div className="flex gap-2">

                                <Tag
                                    value={`${selectedCount} Selected`}
                                    severity="success"
                                />

                                <Tag
                                    value={`${totalCount} Total`}
                                />

                            </div>

                        </div>

                        <Divider className="my-2" />

                        <div className="flex gap-3 text-sm">

                            <span className="text-green-600">
                                <i className="pi pi-check-circle mr-1" />
                                {sentCount} Previously contacted
                            </span>

                            <span className="text-600">
                                <i className="pi pi-user mr-1" />
                                {notSentCount} Never contacted
                            </span>

                        </div>

                    </div>
                )}

                {/* ------------------------------------------ */}
                {/* Exclude options */}
                {/* ------------------------------------------ */}

                {templateName &&
                    !lastWpMessages.loading &&
                    lastWpMessages.contacts.length > 0 && (

                        <div className="border-1 surface-border border-round p-3 mb-3">

                            <div className="flex justify-content-between align-items-center mb-2">

                                <div>
                                    <div className="font-semibold text-900">
                                        Exclude previously contacted
                                    </div>

                                    <small className="text-600">
                                        Don't send this template again within:
                                    </small>
                                </div>

                            </div>

                            <div className="flex gap-2 flex-wrap">

                                <Button
                                    type="button"
                                    label="Today"
                                    icon="pi pi-calendar"
                                    className={
                                        excludePeriod === "today"
                                            ? "p-button-warning"
                                            : "p-button-outlined"
                                    }
                                    onClick={() => excludeContacts("today")}
                                />

                                <Button
                                    type="button"
                                    label="This Week"
                                    icon="pi pi-calendar"
                                    className={
                                        excludePeriod === "week"
                                            ? "p-button-warning"
                                            : "p-button-outlined"
                                    }
                                    onClick={() => excludeContacts("week")}
                                />

                                <Button
                                    type="button"
                                    label="This Month"
                                    icon="pi pi-calendar"
                                    className={
                                        excludePeriod === "month"
                                            ? "p-button-warning"
                                            : "p-button-outlined"
                                    }
                                    onClick={() => excludeContacts("month")}
                                />

                            </div>

                        </div>
                    )}

                {/* ------------------------------------------ */}
                {/* Contacts */}
                {/* ------------------------------------------ */}

                {templateName && (
                    <div className="border-1 surface-border border-round">

                        {/* Toolbar */}
                        <div className="p-3 surface-50 border-bottom-1 surface-border">
                            <div className="flex gap-2 align-items-center">
                                {/* Search */}
                                <div className="flex-1 relative">
                                    <i className="pi pi-search"
                                        style={{
                                            position: "absolute",
                                            left: "12px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1
                                        }}
                                    />
                                    <InputText value={search} onChange={e => setSearch(e.target.value)}
                                        placeholder="Search contact..."
                                        className="w-full"
                                        style={{ paddingLeft: "38px" }}
                                    />
                                </div>
                                {/* Select all */}
                                <Button type="button" label="All"
                                    className="p-button-sm p-button-outlined"
                                    style={{ width: "60px", flex: "0 0 60px" }}
                                    onClick={selectAll}
                                />
                                {/* Clear all */}
                                <Button type="button" label="None"
                                    className="p-button-sm p-button-outlined"
                                    style={{ width: "65px", flex: "0 0 65px" }}
                                    onClick={clearAll}
                                />
                            </div>
                        </div>

                        {/* Loading */}

                        {lastWpMessages.loading && (
                            <div className="p-5 text-center">
                                <i className="pi pi-spin pi-spinner text-2xl mb-2" />
                                <div className="text-600">
                                    Checking previous WhatsApp messages...
                                </div>
                            </div>
                        )}

                        {/* Contact list */}

                        {!lastWpMessages.loading && filteredContacts.length > 0 && (
                            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                                {filteredContacts.map(contact => (
                                    <div key={contact.id}
                                        className="flex align-items-center gap-3 p-3 border-bottom-1 surface-border">
                                        <Checkbox inputId={`contact-${contact.id}`}
                                            checked={contact.selected ?? false}
                                            onChange={e =>
                                                handleContactSelect(contact.id, !!e.checked)
                                            }
                                        />

                                        <label htmlFor={`contact-${contact.id}`} className="flex-1 cursor-pointer">
                                            <div className="font-medium text-900">{contact.name}</div>
                                            <div className="text-sm text-600">{contact.phoneNumber}</div>
                                            {contact.lastWpMessage ? (
                                                <div className="text-xs text-orange-600 mt-1">
                                                    <i className="pi pi-history mr-1" />
                                                    Last sent:
                                                    {" "}
                                                    {showDateTime(
                                                        contact.lastWpMessage.sendAt
                                                    )}
                                                    {" · "}
                                                    {contact.lastWpMessage.wpTemplateName}
                                                </div>
                                            ) : (
                                                <div className="text-xs text-green-600 mt-1">
                                                    <i className="pi pi-check-circle mr-1" />
                                                    No previous message
                                                </div>
                                            )}
                                        </label>
                                        {contact.lastWpMessage && (
                                            <Tag value="Previously sent" severity="warning" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {!lastWpMessages.loading && filteredContacts.length === 0 && (
                            <div className="p-5 text-center text-600">
                                <i className="pi pi-users text-3xl mb-2" />
                                <div>No contacts found</div>
                            </div>
                        )}

                    </div>
                )}

                {/* ------------------------------------------ */}
                {/* Footer */}
                {/* ------------------------------------------ */}

                <div className="flex justify-content-between align-items-center mt-4 gap-2">

                    <Button
                        label="Clear"
                        icon="pi pi-refresh"
                        className="p-button-secondary p-button-outlined"
                        onClick={onClear}
                        type="button"
                    />

                    <Button
                        label={`Send${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
                        icon="pi pi-whatsapp"
                        loading={loading}
                        disabled={loading || !templateName || selectedCount === 0}
                        type="submit"
                    />

                </div>

            </form>

        </Dialog>
    );
};